import React from "react";
import {
    Box,
    Typography,
    TextField,
    Autocomplete,
    CircularProgress,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Stack,
    Card,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { tituloLabelSx } from "./estilos";
import StockAlmacenes from "../ui/StockAlmacenes";
import UbicacionArticulo from "../ui/UbicacionArticulo";

const BUSQUEDA_MIN_CARACTERES = 2;

/**
 * Sección de artículos del formulario de inventario: buscador con debounce
 * (Autocomplete controlado desde el padre) y tabla/tarjetas de líneas ya
 * añadidas. Muestra la ubicación y el stock de TODAS las naves para cada artículo. No maneja
 * estado propio: recibe valores y callbacks por props.
 */
const SeccionArticulos = ({
    opciones,
    buscando,
    q,
    onInputChangeQ,
    onAgregarLinea,
    mostrarErrorLineas,
    errorLineas,
    lineas,
    isMobile,
    lineaResaltada,
    obtenerStock,
    obtenerUbicacion,
    onQuitarLinea,
}) => (
    <>
        <Box>
            <Typography variant="body2" sx={tituloLabelSx}>
                <SearchIcon sx={{ fontSize: 18 }} /> Añadir artículos *
            </Typography>
            <Autocomplete
                options={opciones}
                // Los resultados ya vienen filtrados por el buscador de productos del backend
                filterOptions={(opcionesServidor) => opcionesServidor}
                loading={buscando}
                value={null}
                inputValue={q}
                onInputChange={(_, valorTexto) => onInputChangeQ(valorTexto)}
                onChange={(_, seleccionado) => onAgregarLinea(seleccionado)}
                isOptionEqualToValue={(opcion, valor) => opcion?.referencia === valor?.referencia}
                getOptionLabel={(opcion) => `${opcion?.referencia ?? ""} — ${opcion?.nombre ?? ""}`}
                noOptionsText={q.trim().length < BUSQUEDA_MIN_CARACTERES ? "Escribe al menos 2 caracteres para buscar" : "Sin resultados"}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Referencia, nombre o EAN..."
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {buscando ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                    />
                )}
            />
            {mostrarErrorLineas && errorLineas && (
                <Typography sx={{ fontSize: 12.5, color: "#dc3545", mt: 0.5 }}>{errorLineas}</Typography>
            )}
        </Box>

        {lineas.length > 0 && (isMobile ? (
            <Stack spacing={1.5}>
                {lineas.map((linea) => {
                    return (
                        <Card
                            key={linea.referencia}
                            sx={{
                                p: 1.5,
                                border: "1px solid #e9ecef",
                                backgroundColor: lineaResaltada === linea.referencia ? "#fff3cd" : "#fff",
                                transition: "background-color 0.3s ease",
                            }}
                        >
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{linea.referencia}</Typography>
                                        <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{linea.nombre}</Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <UbicacionArticulo ubicacion={obtenerUbicacion(linea)} />
                                        </Box>
                                    </Box>
                                    <IconButton size="small" onClick={() => onQuitarLinea(linea.referencia)} aria-label="Quitar artículo" sx={{ color: "#dc3545" }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                                <StockAlmacenes stockPorAlmacen={obtenerStock(linea)} titulo="Stock por nave" />
                            </Stack>
                        </Card>
                    );
                })}
            </Stack>
        ) : (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: "#495057" }}>Referencia</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#495057" }}>Nombre</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#495057" }}>Ubicación</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#495057" }}>Stock por nave</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {lineas.map((linea) => {
                            return (
                            <TableRow
                                key={linea.referencia}
                                sx={{
                                    backgroundColor: lineaResaltada === linea.referencia ? "#fff3cd" : "transparent",
                                    transition: "background-color 0.3s ease",
                                }}
                            >
                                <TableCell sx={{ fontSize: 13 }}>{linea.referencia}</TableCell>
                                <TableCell sx={{ fontSize: 13 }}>{linea.nombre}</TableCell>
                                <TableCell>
                                    <UbicacionArticulo ubicacion={obtenerUbicacion(linea)} />
                                </TableCell>
                                <TableCell>
                                    <StockAlmacenes stockPorAlmacen={obtenerStock(linea)} titulo={null} />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => onQuitarLinea(linea.referencia)} aria-label="Quitar artículo" sx={{ color: "#dc3545" }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        ))}
    </>
);

export default SeccionArticulos;
